import React, { useCallback, useEffect, useState } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import { Card, Input, Layout, Spin, Tabs, Tree, Typography, message, Modal, Table, Checkbox, Button, InputNumber } from 'antd';
import type { DataNode } from 'antd/es/tree';
import { guideApi } from '@/services/guide/guide-api';
import { vhWebPartApi } from '@/services/vanHoa/webPart';
import { useAuthStore } from '@/store/useAuthStore';
import { usePrivateStore } from '@/store/usePrivateStore';
import type { GuideAdminViewModel } from '@/types/guide';
import { t } from 'i18next';
import styles from './HelpModal.module.css';
import { VRIO_SCORE_MIN, VRIO_SCORE_MAX, clampVrioScore, parseVrioNguyenTac, buildVrioNguyenTac } from '@/utils/vrioScore';
import { AiContentLabel } from '@/components/common/AiContentLabel';
import { toSafeHtml } from '@/utils/sanitizeHtml'
const { Sider, Content } = Layout;
const { Text } = Typography;

interface TreeDataNode {
  key: string;
  title: string;
  children: TreeDataNode[];
  data: GuideAdminViewModel;
}

interface HelpModalProps {
  visible: boolean;
  onClose: () => void;
  controller?: string;
  yeuToThanhCongNganhData?: Array<{ id: string; noiDung?: string; nguyenTac?: string; fromAi?: boolean; [key: string]: unknown }>;
  /** VH: gọi MS.Culture VHWebPart/gethuongdansudung thay vì KnowledgeBase guide. */
  useVanHoaGuide?: boolean;
  vhStrController?: string;
  initialTarget?: string;
}

const HelpModal: React.FC<HelpModalProps> = ({
  visible,
  onClose,
  controller,
  yeuToThanhCongNganhData,
  useVanHoaGuide = false,
  vhStrController,
  initialTarget,
}) => {
  const { tenantID } = useAuthStore();

  const [loading, setLoading] = useState(true);
  const [treeData, setTreeData] = useState<TreeDataNode[]>([]);
  const [filteredTreeData, setFilteredTreeData] = useState<TreeDataNode[]>([]);
  const [selectedGuide, setSelectedGuide] = useState<GuideAdminViewModel | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');
  const { getRequestHeader } = usePrivateStore();
  // Guide data for suggestion modal
  const [guideView, setGuideView] = useState<{
    modal7S?: Array<Record<string, unknown>>;
    modal7SGroup?: Array<Record<string, unknown>>;
    nganhNghes?: Array<{ id?: string; ten: string; yeuTos?: Array<{ ten: string }> }>;
    modalPEST?: Array<Record<string, unknown>>;
    modalPESTGroup?: Array<Record<string, unknown>>;
    modalMPORTER?: Array<Record<string, unknown>>;
    modalMPORTERGroup?: Array<Record<string, unknown>>;
  } | null>(null);

  // State for 7S selection (read-only in HelpModal)
  const [selectedSevenS, setSelectedSevenS] = useState<Record<string, { 
    strong?: boolean; 
    coHoi?: boolean; 
    thachThuc?: boolean; 
    chon?: boolean;
    V?: number;
    R?: number;
    I?: number;
    O?: number;
    content: string 
  }>>({});

  // Handle apply 7S selection
  const handleApplySevenSSelection = useCallback((targetKey?: string) => {
    // Collect selected items from selectedSevenS
    const selectedItems = Object.entries(selectedSevenS)
      .filter(([, val]) => !!val?.strong && val?.content?.trim())
      .map(([key, val]) => ({ key, content: val.content }));

    if (selectedItems.length === 0) {
      message.warning(t('bsc.msg.vuilongchonitnhatmuc', 'Vui lòng chọn ít nhất một mục'));
      return;
    }

    // Always dispatch event for pages that listen to it (like Buoc2SwotPage)
    localStorage.setItem('helpModalSevenSSelected', JSON.stringify({ selectedItems, targetKey }));
    window.dispatchEvent(new CustomEvent('helpModalSevenSSelected', { 
      detail: { selectedItems, targetKey } 
    }));

    message.success(t('bsc.msg.dathecountmuc', 'Đã thêm {{count}} mục', { count: selectedItems.length }));

    // Clear selections after applying
    setSelectedSevenS({});
  }, [selectedSevenS]);

  // Handle apply PEST/PORTER selection with separate coHoi and thachThuc
  const handleApplyPestPorterSelection = useCallback(() => {
    // Collect selected items for coHoi (opportunities)
    const coHoiItems = Object.entries(selectedSevenS)
      .filter(([, val]) => !!val?.coHoi && val?.content?.trim())
      .map(([key, val]) => ({ key, content: val.content }));

    // Collect selected items for thachThuc (challenges)
    const thachThucItems = Object.entries(selectedSevenS)
      .filter(([, val]) => !!val?.thachThuc && val?.content?.trim())
      .map(([key, val]) => ({ key, content: val.content }));

    if (coHoiItems.length === 0 && thachThucItems.length === 0) {
      message.warning(t('bsc.msg.vuilongchonitnhatmuc', 'Vui lòng chọn ít nhất một mục'));
      return;
    }

    // Always dispatch events for pages that listen to it (like Buoc2SwotPage)
    // Dispatch separate events for coHoi and thachThuc
    if (coHoiItems.length > 0) {
      localStorage.setItem('helpModalSevenSSelected', JSON.stringify({ selectedItems: coHoiItems, targetKey: 'coHoi' }));
      window.dispatchEvent(new CustomEvent('helpModalSevenSSelected', { 
        detail: { selectedItems: coHoiItems, targetKey: 'coHoi' } 
      }));
    }

    if (thachThucItems.length > 0) {
      localStorage.setItem('helpModalSevenSSelected', JSON.stringify({ selectedItems: thachThucItems, targetKey: 'thachThuc' }));
      window.dispatchEvent(new CustomEvent('helpModalSevenSSelected', { 
        detail: { selectedItems: thachThucItems, targetKey: 'thachThuc' } 
      }));
    }

    const totalItems = coHoiItems.length + thachThucItems.length;
    message.success(t('bsc.msg.dathecountmuc', 'Đã thêm {{count}} mục', { count: totalItems }));

    // Clear selections after applying
    setSelectedSevenS({});
  }, [selectedSevenS]);

  // Handle apply VRIO selection
  const persistVrioToYeuToThanhCongNganh = useCallback(
    (id: string, scores: { chon: boolean; V: number; R: number; I: number; O: number }) => {
      const vrioNguyenTac = buildVrioNguyenTac(scores.V, scores.R, scores.I, scores.O, scores.chon);
      const updated = (yeuToThanhCongNganhData || []).map((item) =>
        item.id === id ? { ...item, nguyenTac: vrioNguyenTac } : item
      );
      window.dispatchEvent(new CustomEvent('yeuToThanhCongNganhDataUpdate', {
        detail: { data: updated },
      }));
    },
    [yeuToThanhCongNganhData]
  );

  const initVrioSelectionsFromYeuTo = useCallback(() => {
    const initial: Record<string, {
      strong?: boolean;
      coHoi?: boolean;
      thachThuc?: boolean;
      chon?: boolean;
      V?: number;
      R?: number;
      I?: number;
      O?: number;
      content: string;
    }> = {};
    (yeuToThanhCongNganhData || []).forEach((it) => {
      if (!it?.id) return;
      const parsed = parseVrioNguyenTac(it.nguyenTac);
      initial[it.id] = {
        chon: parsed?.chon ?? false,
        V: parsed?.V ?? 0,
        R: parsed?.R ?? 0,
        I: parsed?.I ?? 0,
        O: parsed?.O ?? 0,
        content: '',
      };
    });
    setSelectedSevenS(initial);
  }, [yeuToThanhCongNganhData]);

  useEffect(() => {
    if (visible && selectedGuide?.modalGoiY === 'VRIO') {
      initVrioSelectionsFromYeuTo();
    }
  }, [visible, selectedGuide?.id, selectedGuide?.modalGoiY, initVrioSelectionsFromYeuTo]);

  const handleApplyVrioSelection = useCallback(() => {
    const selectedIds = Object.entries(selectedSevenS)
      .filter(([, val]) => !!val?.chon)
      .map(([key]) => key);

    if (selectedIds.length === 0) {
      message.warning(t('bsc.msg.vuilongchonitnhatmuc', 'Vui lòng chọn ít nhất một mục'));
      return;
    }

    const selectedItems = selectedIds.map((id) => {
      const src = yeuToThanhCongNganhData?.find((x) => x.id === id);
      const selection = selectedSevenS[id];

      return {
        key: id,
        content: src?.noiDung || '',
        vrioData: {
          V: clampVrioScore(selection?.V),
          R: clampVrioScore(selection?.R),
          I: clampVrioScore(selection?.I),
          O: clampVrioScore(selection?.O),
        },
      };
    });

    // Dispatch event for VRIO selection
    window.dispatchEvent(new CustomEvent('helpModalVrioSelected', { 
      detail: { selectedItems } 
    }));

    message.success(t('bsc.msg.dathecountmuc', 'Đã thêm {{count}} mục', { count: selectedItems.length }));

    // Clear selections after applying
    setSelectedSevenS({});
  }, [selectedSevenS, yeuToThanhCongNganhData]);

  const hasMeaningfulGuideField = (value?: string | null): boolean => {
    const text = String(value ?? '').trim();
    return text.length > 0 && text !== '0';
  };

  const normalizeGuideList = (list: unknown[]): GuideAdminViewModel[] =>
    list.map((raw) => {
      const row = (raw || {}) as Record<string, unknown>;
      const modalGoiY = (row.modalGoiY ?? row.ModalGoiY) as string | null | undefined;
      const soTrangTaiLieu = (row.soTrangTaiLieu ?? row.SoTrangTaiLieu) as string | null | undefined;
      const hasGoiYRaw = Boolean(row.hasGoiY ?? row.HasGoiY);
      const hasTaiLieuRaw = Boolean(row.hasTaiLieu ?? row.HasTaiLieu);
      return {
        id: String(row.id ?? row.ID ?? ''),
        ten: String(row.ten ?? row.Ten ?? ''),
        parentID: String(row.parentID ?? row.ParentID ?? '0'),
        noiDung: (row.noiDung ?? row.NoiDung) as string | null | undefined,
        noiDungMoi: (row.noiDungMoi ?? row.NoiDungMoi) as string | null | undefined,
        viTri: Number(row.viTri ?? row.ViTri ?? 0),
        controller: (row.controller ?? row.Controller) as string | null | undefined,
        target: (row.target ?? row.Target) as string | null | undefined,
        linkVideo: (row.linkVideo ?? row.LinkVideo) as string | null | undefined,
        linkVideoHuongDan: (row.linkVideoHuongDan ?? row.LinkVideoHuongDan) as string | null | undefined,
        modalGoiY: hasMeaningfulGuideField(modalGoiY) ? modalGoiY : null,
        soTrangTaiLieu: hasMeaningfulGuideField(soTrangTaiLieu) ? soTrangTaiLieu : null,
        hasVideo: Boolean(row.hasVideo ?? row.HasVideo),
        hasVideoHuongDan: Boolean(row.hasVideoHuongDan ?? row.HasVideoHuongDan),
        hasTaiLieu: hasTaiLieuRaw && hasMeaningfulGuideField(soTrangTaiLieu),
        hasHuongDan: Boolean(row.hasHuongDan ?? row.HasHuongDan),
        hasGoiY: hasGoiYRaw && hasMeaningfulGuideField(modalGoiY),
        targetGet: (row.targetGet ?? row.TargetGet) as string | null | undefined,
      };
    });

  const findTreeNodeByTarget = (nodes: TreeDataNode[], target: string): TreeDataNode | null => {
    for (const node of nodes) {
      const data = (node as TreeDataNode & { data?: GuideAdminViewModel }).data;
      if (data?.target === target || data?.targetGet === target) {
        return node;
      }
      if (node.children?.length) {
        const found = findTreeNodeByTarget(node.children as TreeDataNode[], target);
        if (found) return found;
      }
    }
    return null;
  };

  const loadGuideData = useCallback(async () => {
    try {
      if(!tenantID) return;
      setLoading(true);
      const strController = useVanHoaGuide ? (vhStrController || controller) : controller;
      const payload = {
        site: tenantID,
        requestHeader: getRequestHeader(),
        strController,
        txtYeuToThanhCong: useVanHoaGuide ? initialTarget : undefined,
      };
      const response = (useVanHoaGuide
        ? await vhWebPartApi.getHuongDanSuDung(payload)
        : await guideApi.getHuongDanSuDung(payload)) as unknown as { success?: boolean; data?: { listHuongDan?: unknown[] } } | null;
      const listHuongDan: GuideAdminViewModel[] = normalizeGuideList(
        (response?.data?.listHuongDan as unknown[]) || [],
      );
      if (Array.isArray(listHuongDan)) {
        const t = convertToTreeData(listHuongDan as GuideAdminViewModel[]);
        setTreeData(t);
        setFilteredTreeData(t);
        setExpandedKeys((listHuongDan as GuideAdminViewModel[]).map((x) => x.id));

        const targetKey = initialTarget?.trim();
        const targetNode = targetKey ? findTreeNodeByTarget(t, targetKey) : null;
        const pickNode = targetNode ?? (t[0]?.children?.length ? t[0].children[0] : null);
        if (pickNode?.data) {
          setSelectedGuide(pickNode.data);
          setSelectedKeys([pickNode.key]);
        }
        
        // Store guide data for suggestion modal
        const data = response?.data as unknown as {
          modal7S?: Array<Record<string, unknown>>;
          modal7SGroup?: Array<Record<string, unknown>>;
          nganhNghes?: Array<{ id?: string; ten: string; yeuTos?: Array<{ ten: string }> }>;
          modalPEST?: Array<Record<string, unknown>>;
          modalPESTGroup?: Array<Record<string, unknown>>;
          modalMPORTER?: Array<Record<string, unknown>>;
          modalMPORTERGroup?: Array<Record<string, unknown>>;
        };
        setGuideView({
          modal7S: data?.modal7S,
          modal7SGroup: data?.modal7SGroup,
          nganhNghes: data?.nganhNghes,
          modalPEST: data?.modalPEST,
          modalPESTGroup: data?.modalPESTGroup,
          modalMPORTER: data?.modalMPORTER,
          modalMPORTERGroup: data?.modalMPORTERGroup,
        });
      }
      } catch {
        message.error(t('bsc.msg.khongthetaidulieuhuongdan', 'Không thể tải dữ liệu hướng dẫn'));
      } finally {
      setLoading(false);
    }
  }, [tenantID, controller, useVanHoaGuide, vhStrController, initialTarget, getRequestHeader]);

  const convertToTreeData = (data: GuideAdminViewModel[]): TreeDataNode[] => {
    const dataMap = new Map<string, TreeDataNode>();
    const result: TreeDataNode[] = [];
    data.forEach((item) => {
      // Ensure key is always a string
      const key = String(item.id);
      dataMap.set(key, { key, title: item.ten, children: [], data: item });
    });
    data.forEach((item) => {
      const key = String(item.id);
      const treeItem = dataMap.get(key)!;
      const parentKey = String(item.parentID);
      const parent = dataMap.get(parentKey);

      if (!parent) result.push(treeItem);
      else {
        parent.children.push(treeItem);
      }
    });
    return result;
  };

  // Load data when modal opens
  useEffect(() => {
    if (visible && tenantID) {
      loadGuideData();
    }
  }, [visible, tenantID, loadGuideData]);

  const handleTreeSelect = (keys: React.Key[]) => {
    if (keys.length > 0) {
      const selectedKey = keys[0] as string;
      setSelectedKeys(keys as string[]);
      const findGuideData = (nodes: TreeDataNode[]): GuideAdminViewModel | null => {
        for (const node of nodes) {
          if (node.key === selectedKey) return node.data;
          if (node.children && node.children.length > 0) {
            const found = findGuideData(node.children);
            if (found) return found;
          }
        }
        return null;
      };
      setSelectedGuide(findGuideData(treeData));
    }
  };

  const handleTreeExpand = (keys: React.Key[]) => setExpandedKeys(keys as string[]);

  const handleSearch = (value: string) => {
    setSearchValue(value);
    if (!value.trim()) {
      setFilteredTreeData(treeData);
      return;
    }
    const filterTreeData = (nodes: TreeDataNode[]): TreeDataNode[] =>
      nodes
        .filter((node) => {
          const title = node.title.toLowerCase();
          const searchTerm = value.toLowerCase();
          const matchesCurrent = title.includes(searchTerm);
          const filteredChildren = node.children ? filterTreeData(node.children) : [];
          return matchesCurrent || filteredChildren.length > 0;
        })
        .map((node) => ({ ...node, children: node.children ? filterTreeData(node.children) : [] }));
    const filtered = filterTreeData(treeData);
    setFilteredTreeData(filtered);
    const getAllKeys = (nodes: TreeDataNode[]): string[] => {
      let keys: string[] = [];
      nodes.forEach((node) => {
        keys.push(node.key);
        if (node.children && node.children.length > 0) keys = keys.concat(getAllKeys(node.children));
      });
      return keys;
    };
    if (filtered.length > 0) setExpandedKeys(getAllKeys(filtered));
  };

  const toAntTreeNodes = (nodes: TreeDataNode[]): DataNode[] =>
    nodes.map((n) => ({
      key: n.key,
      title: n.title,
      children: n.children && n.children.length > 0 ? toAntTreeNodes(n.children) : undefined,
    }));

  const getYouTubeVideoId = (url: string): string | null => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  const renderYouTubePlayer = (url: string) => {
    const videoId = getYouTubeVideoId(url);
    if (!videoId) return <div className={styles['help-empty-state']}><Text type="secondary">{t('bsc.msg.khongcovideohuongdan', 'Không có video hướng dẫn')}</Text></div>;
    return (
      <div className={styles['youtube-player-container']}>
        <iframe width="100%" height="100%" src={`https://www.youtube.com/embed/${videoId}`} title="YouTube video player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
      </div>
    );
  };

  const extractPdfUrl = (htmlOrUrl?: string | null): string | null => {
    if (!htmlOrUrl) return null;
    const trimmed = htmlOrUrl.trim();
    if (/^https?:\/\//i.test(trimmed)) {
      return /\.pdf(\?|#|$)/i.test(trimmed) ? trimmed : null;
    }
    const linkMatch = trimmed.match(/(https?:[^'"\s>]+\.pdf)/i);
    if (linkMatch && linkMatch[1]) return linkMatch[1];
    return null;
  };

  const renderOperationGuide = (content?: string | null) => {
    const pdfUrl = extractPdfUrl(content ?? '');
    if (pdfUrl) {
      return (
        <div className={styles['operation-iframe-container']}>
          <iframe src={pdfUrl} title="Operation Guide" width="100%" height="100%" frameBorder={0} />
        </div>
      );
    }
    if (content) {
      return (
        <div className={styles['guide-content-card']}>
          <div className={styles['operation-content']} dangerouslySetInnerHTML={toSafeHtml(content)} />
        </div>
      );
    }
    return <div className={styles['help-empty-state']}><Text type="secondary">{t('bsc.msg.khongconoidunghuongdanthaotac', 'Không có nội dung hướng dẫn thao tác')}</Text></div>;
  };

  const renderInstructionVideo = (url?: string | null) => {
    if (!url) {
      return <div className={styles['help-empty-state']}><Text type="secondary">{t('bsc.msg.khongcovideohuongdan', 'Không có video hướng dẫn')}</Text></div>;
    }
    return renderYouTubePlayer(url);
  };

  const renderDocument = (page?: string | number | null) => {
    if (page === undefined || page === null || page === '') {
      return <div className={styles['help-empty-state']}><Text type="secondary">{t('bsc.msg.khongcotailieu', 'Không có tài liệu')}</Text></div>;
    }
    const currentLanguage = 'vi'; // Default to Vietnamese
    const english = currentLanguage !== 'vi' ? '_en' : '';
    const pdfUrl = `https://bsc.toppion.com.vn/uploads/files/TailieuBSC${english}.pdf#page=${page}`;
    return (
      <div className={styles['operation-iframe-container']}>
        <iframe src={pdfUrl} title="Document" width="100%" height="100%" frameBorder={0} />
      </div>
    );
  };

  const renderSuggestion = (modalGoiY?: string | null) => {
    if (!hasMeaningfulGuideField(modalGoiY)) {
      return <div className={styles['help-empty-state']}><Text type="secondary">{t('bsc.msg.khongcogoiy', 'Không có gợi ý')}</Text></div>;
    }
    
    // Check if it's 7S Diem Manh suggestion
    if (modalGoiY === '7SDiemManh') {
      return (
        <div className="w-full h-full bg-white rounded-lg">
          {(() => {
            // Chuẩn hoá dữ liệu cho AntD Table
            type SevenSRow = { key: string; level: 'group' | 'item'; name: string; indexPath: string };
            const groups = (guideView?.modal7SGroup || []) as Array<Record<string, unknown>>;
            const items = (guideView?.modal7S || []) as Array<Record<string, unknown>>;
            const rows: SevenSRow[] = [];
            groups?.forEach((group, gi) => {
              const gid = String(group?.id ?? '');
              const gname = group?.name ?? '';
              rows.push({ key: `g-${gid}`, level: 'group', name: `${gi+1}. ${gname}`, indexPath: `${gi+1}.` });
              const inGroup = items.filter(it => {
                // Bám sát logic cũ: i.Content.Equals(groupID)
                const cid = String((it as Record<string, unknown>)?.content ?? '');
                return cid === gid;
              });
              inGroup.forEach((it, ii) => {
                const iname = it?.name ?? '';
                rows.push({ key: `g-${gid}-i-${ii}`, level: 'item', name: `${gi+1}.${ii+1}. ${iname}`, indexPath: `${gi+1}.${ii+1}.` });
              });
            });

            const columns = [
              {
                title: t('bsc.label.mohinh7s', 'Mô Hình 7S'),
                dataIndex: 'name',
                key: 'name',
                render: (text: string, record: SevenSRow) => (
                  <div style={{ paddingLeft: record.level === 'item' ? 30 : 0, fontWeight: record.level === 'group' ? 600 : 400 }}>{text}</div>
                ),
                width: '45%'
              },
              {
                title: t('bsc.label.diemmanh', 'Điểm mạnh'),
                key: 'strong',
                align: 'center' as const,
                render: (_: unknown, record: SevenSRow) => record.level === 'item' ? (
                  <Checkbox
                    checked={!!selectedSevenS[record.key]?.strong}
                    onChange={(e) => setSelectedSevenS(prev => {
                      if (e.target.checked) {
                        // When checking, add/update the entry
                        return { 
                          ...prev, 
                          [record.key]: { 
                            strong: true, 
                            content: prev[record.key]?.content || record.name?.replace(/^[0-9.\s]+/, '') || '' 
                          } 
                        };
                      } else {
                        // When unchecking, remove the entry
                        const newState = { ...prev };
                        delete newState[record.key];
                        return newState;
                      }
                    })}
                  />
                ) : null,
                width: 100,
              },
              {
                title: t('bsc.label.noidung', 'Nội dung'),
                key: 'content',
                render: (_: unknown, record: SevenSRow) => record.level === 'item' && selectedSevenS[record.key]?.strong ? (
                  <Input.TextArea
                    autoSize={{ minRows: 1, maxRows: 4 }}
                    value={selectedSevenS[record.key]?.content || record.name?.replace(/^[0-9.\s]+/, '') || ''}
                    onChange={(e) => setSelectedSevenS(prev => ({ ...prev, [record.key]: { strong: !!prev[record.key]?.strong, content: e.target.value } }))}
                  />
                ) : null,
              },
            ];

            return (
              <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Table
                  size="small"
                  pagination={false}
                  rowKey="key"
                  columns={columns}
                  dataSource={rows}
                  rowClassName={(r: SevenSRow) => r.level === 'group' ? 'active' : ''}
                  scroll={{ y: 'calc(100vh - 340px)' }}
                />
                <div style={{ padding: '16px', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end' }}>
                  <Button 
                    type="primary" 
                    onClick={() => handleApplySevenSSelection('diemManh')}
                    disabled={Object.values(selectedSevenS).filter(val => val?.strong && val?.content?.trim()).length === 0}
                  >
                    {t('bsc.btn.chon', 'Chọn')}
                  </Button>
                </div>
              </div>
            );
          })()}
        </div>
      );
    }

    // Check if it's 7S Diem Yeu suggestion
    if (modalGoiY === '7SDiemYeu') {
      return (
        <div className="w-full h-full bg-white rounded-lg">
          {(() => {
            // Chuẩn hoá dữ liệu cho AntD Table
            type SevenSRow = { key: string; level: 'group' | 'item'; name: string; indexPath: string };
            const groups = (guideView?.modal7SGroup || []) as Array<Record<string, unknown>>;
            const items = (guideView?.modal7S || []) as Array<Record<string, unknown>>;
            const rows: SevenSRow[] = [];
            groups?.forEach((group, gi) => {
              const gid = String(group?.id ?? '');
              const gname = group?.name ?? '';
              rows.push({ key: `g-${gid}`, level: 'group', name: `${gi+1}. ${gname}`, indexPath: `${gi+1}.` });
              const inGroup = items.filter(it => {
                // Bám sát logic cũ: i.Content.Equals(groupID)
                const cid = String((it as Record<string, unknown>)?.content ?? '');
                return cid === gid;
              });
              inGroup.forEach((it, ii) => {
                const iname = it?.name ?? '';
                rows.push({ key: `g-${gid}-i-${ii}`, level: 'item', name: `${gi+1}.${ii+1}. ${iname}`, indexPath: `${gi+1}.${ii+1}.` });
              });
            });

            const columns = [
              {
                title: t('bsc.label.mohinh7s', 'Mô Hình 7S'),
                dataIndex: 'name',
                key: 'name',
                render: (text: string, record: SevenSRow) => (
                  <div style={{ paddingLeft: record.level === 'item' ? 30 : 0, fontWeight: record.level === 'group' ? 600 : 400 }}>{text}</div>
                ),
                width: '45%'
              },
              {
                title: t('bsc.label.diemyeu', 'Điểm yếu'),
                key: 'weak',
                align: 'center' as const,
                render: (_: unknown, record: SevenSRow) => record.level === 'item' ? (
                  <Checkbox
                    checked={!!selectedSevenS[record.key]?.strong}
                    onChange={(e) => setSelectedSevenS(prev => {
                      if (e.target.checked) {
                        // When checking, add/update the entry
                        return { 
                          ...prev, 
                          [record.key]: { 
                            strong: true, 
                            content: prev[record.key]?.content || record.name?.replace(/^[0-9.\s]+/, '') || '' 
                          } 
                        };
                      } else {
                        // When unchecking, remove the entry
                        const newState = { ...prev };
                        delete newState[record.key];
                        return newState;
                      }
                    })}
                  />
                ) : null,
                width: 100,
              },
              {
                title: t('bsc.label.noidung', 'Nội dung'),
                key: 'content',
                render: (_: unknown, record: SevenSRow) => record.level === 'item' && selectedSevenS[record.key]?.strong ? (
                  <Input.TextArea
                    autoSize={{ minRows: 1, maxRows: 4 }}
                    value={selectedSevenS[record.key]?.content || record.name?.replace(/^[0-9.\s]+/, '') || ''}
                    onChange={(e) => setSelectedSevenS(prev => ({ ...prev, [record.key]: { strong: !!prev[record.key]?.strong, content: e.target.value } }))}
                  />
                ) : null,
              },
            ];

            return (
              <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Table
                  size="small"
                  pagination={false}
                  rowKey="key"
                  columns={columns}
                  dataSource={rows}
                  rowClassName={(r: SevenSRow) => r.level === 'group' ? 'active' : ''}
                  scroll={{ y: 'calc(100vh - 340px)' }}
                />
                <div style={{ padding: '16px', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end' }}>
                  <Button 
                    type="primary" 
                    onClick={() => handleApplySevenSSelection('diemYeu')}
                    disabled={Object.values(selectedSevenS).filter(val => val?.strong && val?.content?.trim()).length === 0}
                  >
                    {t('bsc.btn.chon', 'Chọn')}
                  </Button>
                </div>
              </div>
            );
          })()}
        </div>
      );
    }

    // Check if it's PEST/PORTER suggestion
    if (modalGoiY === 'modalPESTPORTER') {
      return (
        <div className="w-full h-full bg-white rounded-lg">
          {(() => {
            // Build PEST/PORTER table data
            const pestGroups = (guideView?.modalPESTGroup || []) as Array<Record<string, unknown>>;
            const pestItems = (guideView?.modalPEST || []) as Array<Record<string, unknown>>;
            const porterGroups = (guideView?.modalMPORTERGroup || []) as Array<Record<string, unknown>>;
            const porterItems = (guideView?.modalMPORTER || []) as Array<Record<string, unknown>>;
            
            type PestPorterRow = { key: string; level: 'section' | 'group' | 'item'; name: string; indexPath: string; section: 'pest' | 'porter' };
            const rows: PestPorterRow[] = [];
            
            // Add PEST section header
            rows.push({ key: 'pest-header', level: 'section', name: 'Mô hình PEST', indexPath: '', section: 'pest' });
            
            // Add PEST groups and items
            pestGroups?.forEach((group, gi) => {
              const gid = String(group?.id ?? '');
              const gname = group?.name ?? '';
              rows.push({ key: `pest-g-${gid}`, level: 'group', name: `${gi+1}. ${gname}`, indexPath: `${gi+1}.`, section: 'pest' });
              
              const inGroup = pestItems.filter(it => {
                const cid = String((it as Record<string, unknown>)?.content ?? '');
                return cid === gid;
              });
              
              inGroup.forEach((it, ii) => {
                const iname = it?.name ?? '';
                rows.push({ key: `pest-g-${gid}-i-${ii}`, level: 'item', name: `${gi+1}.${ii+1}. ${iname}`, indexPath: `${gi+1}.${ii+1}.`, section: 'pest' });
              });
            });
            
            // Add PORTER section header
            rows.push({ key: 'porter-header', level: 'section', name: 'Mô hình 5 FORCES', indexPath: '', section: 'porter' });
            
            // Add PORTER groups and items
            porterGroups?.forEach((group, gi) => {
              const gid = String(group?.id ?? '');
              const gname = group?.name ?? '';
              rows.push({ key: `porter-g-${gid}`, level: 'group', name: `${gi+1}. ${gname}`, indexPath: `${gi+1}.`, section: 'porter' });
              
              const inGroup = porterItems.filter(it => {
                const cid = String((it as Record<string, unknown>)?.content ?? '');
                return cid === gid;
              });
              
              inGroup.forEach((it, ii) => {
                const iname = it?.name ?? '';
                rows.push({ key: `porter-g-${gid}-i-${ii}`, level: 'item', name: `${gi+1}.${ii+1}. ${iname}`, indexPath: `${gi+1}.${ii+1}.`, section: 'porter' });
              });
            });

            const columns = [
              {
                title: t('bsc.label.yeuto', 'Yếu tố'),
                dataIndex: 'name',
                key: 'name',
                render: (text: string, record: PestPorterRow) => {
                  if (record.level === 'section') {
                    return <div style={{ padding: '5px', fontWeight: 'bold' }}>{text}</div>;
                  }
                  if (record.level === 'group') {
                    return <div style={{ paddingLeft: '15px', fontWeight: 'bold' }}>{text}</div>;
                  }
                  return <div style={{ paddingLeft: 30 }}>{text}</div>;
                },
                width: '50%'
              },
              {
                title: t('bsc.label.cohoi', 'Cơ hội'),
                key: 'coHoi',
                width: 100,
                align: 'center' as const,
                render: (_: unknown, record: PestPorterRow) => record.level === 'item' ? (
                  <Checkbox
                    checked={!!selectedSevenS[record.key]?.coHoi}
                    onChange={(e) => setSelectedSevenS(prev => {
                      if (e.target.checked) {
                        // Khi chọn Cơ hội, tự động bỏ Thách thức
                        return { 
                          ...prev, 
                          [record.key]: { 
                            coHoi: true,
                            thachThuc: false, // Đảm bảo Thách thức bị bỏ
                            content: prev[record.key]?.content || record.name?.replace(/^[0-9.\s]+/, '') || '' 
                          } 
                        };
                      } else {
                        const newState = { ...prev };
                        if (newState[record.key]) {
                          newState[record.key] = { ...newState[record.key], coHoi: false };
                          if (!newState[record.key].coHoi && !newState[record.key].thachThuc) {
                            delete newState[record.key];
                          }
                        }
                        return newState;
                      }
                    })}
                  />
                ) : null,
              },
              {
                title: t('bsc.label.thachthuc', 'Thách thức'),
                key: 'thachThuc',
                width: 100,
                align: 'center' as const,
                render: (_: unknown, record: PestPorterRow) => record.level === 'item' ? (
                  <Checkbox
                    checked={!!selectedSevenS[record.key]?.thachThuc}
                    onChange={(e) => setSelectedSevenS(prev => {
                      if (e.target.checked) {
                        // Khi chọn Thách thức, tự động bỏ Cơ hội
                        return { 
                          ...prev, 
                          [record.key]: { 
                            coHoi: false, // Đảm bảo Cơ hội bị bỏ
                            thachThuc: true, 
                            content: prev[record.key]?.content || record.name?.replace(/^[0-9.\s]+/, '') || '' 
                          } 
                        };
                      } else {
                        const newState = { ...prev };
                        if (newState[record.key]) {
                          newState[record.key] = { ...newState[record.key], thachThuc: false };
                          if (!newState[record.key].coHoi && !newState[record.key].thachThuc) {
                            delete newState[record.key];
                          }
                        }
                        return newState;
                      }
                    })}
                  />
                ) : null,
              },
              {
                title: t('bsc.label.noidung', 'Nội dung'),
                key: 'content',
                render: (_: unknown, record: PestPorterRow) => record.level === 'item' && (selectedSevenS[record.key]?.coHoi || selectedSevenS[record.key]?.thachThuc) ? (
                  <Input.TextArea
                    autoSize={{ minRows: 1, maxRows: 4 }}
                    value={selectedSevenS[record.key]?.content || record.name?.replace(/^[0-9.\s]+/, '') || ''}
                    onChange={(e) => setSelectedSevenS(prev => ({ 
                      ...prev, 
                      [record.key]: { 
                        ...prev[record.key],
                        content: e.target.value 
                      } 
                    }))}
                  />
                ) : null,
              },
            ];

            return (
              <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Table
                  size="small"
                  pagination={false}
                  rowKey="key"
                  columns={columns}
                  dataSource={rows}
                  rowClassName={(r: PestPorterRow) => r.level === 'group' ? 'active' : ''}
                  scroll={{ y: 'calc(100vh - 340px)' }}
                />
                <div style={{ padding: '16px', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end' }}>
                  <Button 
                    type="primary" 
                    onClick={handleApplyPestPorterSelection}
                    disabled={Object.values(selectedSevenS).filter(val => (val?.coHoi || val?.thachThuc) && val?.content?.trim()).length === 0}
                  >
                    {t('bsc.btn.chon', 'Chọn')}
                  </Button>
                </div>
              </div>
            );
          })()}
        </div>
      );
    }

    // VRIO suggestion content
    if (modalGoiY === 'VRIO') {
      return (
        <div className="w-full h-full bg-white rounded-lg">
          {(() => {
            type VrioRow = { key: string; index: number; ten: string; fromAi?: boolean };
            const rows: VrioRow[] = (yeuToThanhCongNganhData || [])
              .filter((it) => !!it?.id)
              .map((it, idx) => ({
                key: it.id,
                index: idx + 1,
                ten: (it.noiDung || '').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim(),
                fromAi: !!it.fromAi || String(it.id || '').startsWith('temp-ai-'),
              }));

            const vrioRowDefaults = { chon: false, V: 0, R: 0, I: 0, O: 0, content: '' };

            const getSel = (key: string) => ({ ...vrioRowDefaults, ...selectedSevenS[key] });

            const update = (key: string, patch: Partial<{ chon: boolean; V: number; R: number; I: number; O: number; content: string }>) => {
              const next = { ...getSel(key), ...patch };
              setSelectedSevenS((prev) => ({ ...prev, [key]: next }));
              if (
                patch.chon !== undefined ||
                patch.V !== undefined ||
                patch.R !== undefined ||
                patch.I !== undefined ||
                patch.O !== undefined
              ) {
                persistVrioToYeuToThanhCongNganh(key, next);
              }
            };

            const renderVrioScoreInput = (record: VrioRow, field: 'V' | 'R' | 'I' | 'O') => {
              const current = getSel(record.key)[field];
              return (
                <InputNumber
                  min={VRIO_SCORE_MIN}
                  max={VRIO_SCORE_MAX}
                  step={1}
                  precision={0}
                  value={current}
                  onChange={(value) => update(record.key, { [field]: clampVrioScore(value) })}
                  onBlur={() => update(record.key, { [field]: clampVrioScore(current) })}
                  parser={(displayValue) => {
                    const digits = String(displayValue ?? '').replace(/[^\d]/g, '');
                    if (!digits) return VRIO_SCORE_MIN;
                    return clampVrioScore(Number(digits));
                  }}
                  onKeyDown={(e) => {
                    if (['-', '+', 'e', 'E', '.', ','].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  style={{ width: 64 }}
                />
              );
            };

            const columns = [
              {
                title: <div style={{ fontSize: 16, fontWeight: 600 }}>{t('bsc.kienthucloai.yeu-to-thanh-cong-nganh', 'Yếu tố đánh giá/ Yếu tố thành công ngành')}</div>,
                dataIndex: 'ten',
                key: 'ten',
                render: (text: string, record: VrioRow) => (
                  <div>
                    {record.fromAi ? <AiContentLabel /> : null}
                    <strong>{record.index}. {text}</strong>
                  </div>
                ),
              },
              {
                title: <div style={{ fontSize: 16, fontWeight: 600, textAlign: 'center' }}>{t('bsc.btn.chon', 'Chọn')}</div>,
                key: 'chon',
                width: 100,
                align: 'center' as const,
                render: (_: unknown, record: VrioRow) => (
                  <Checkbox checked={getSel(record.key).chon} onChange={(e) => update(record.key, { chon: e.target.checked })} />
                ),
              },
              {
                title: <div><strong style={{ color: '#ffe082', fontSize: '16px' }}>V</strong><br/>Value<br/>{t('bsc.label.giatri', 'Giá trị')}</div>,
                key: 'V',
                width: 90,
                align: 'center' as const,
                render: (_: unknown, record: VrioRow) => renderVrioScoreInput(record, 'V'),
              },
              {
                title: <div><strong style={{ color: '#ffe082', fontSize: '16px' }}>R</strong><br/>Rare<br/>{t('bsc.label.hiem', 'Hiếm')}</div>,
                key: 'R',
                width: 90,
                align: 'center' as const,
                render: (_: unknown, record: VrioRow) => renderVrioScoreInput(record, 'R'),
              },
              {
                title: <div><strong style={{ color: '#ffe082', fontSize: '16px' }}>I</strong><br/>Costly to Imitate<br/>{t('bsc.label.tonnguonlucdebatchuc', 'Tốn nguồn lực để bắt chước')}</div>,
                key: 'I',
                width: 150,
                align: 'center' as const,
                render: (_: unknown, record: VrioRow) => renderVrioScoreInput(record, 'I'),
              },
              {
                title: <div><strong style={{ color: '#ffe082', fontSize: '16px' }}>O</strong><br/>Properly Organized<br/>{t('bsc.label.tinhsansangcuahethong', 'Tính sẵn sàng của hệ thống')}</div>,
                key: 'O',
                width: 160,
                align: 'center' as const,
                render: (_: unknown, record: VrioRow) => renderVrioScoreInput(record, 'O'),
              },
              {
                title: <div style={{ fontSize: 16, fontWeight: 600, textAlign: 'center' }}>{t('bsc.label.tongdiem', 'Tổng điểm')}</div>,
                key: 'sum',
                width: 120,
                align: 'center' as const,
                render: (_: unknown, record: VrioRow) => {
                  const s = getSel(record.key);
                  const sum =
                    (Number(s.V) || 0) + (Number(s.R) || 0) + (Number(s.I) || 0) + (Number(s.O) || 0);
                  const display = Math.round(sum * 100) / 100;
                  return <span style={{ color: 'red', fontWeight: 600 }}>{display}</span>;
                },
              },
            ];

            return (
              <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Table
                  size="small"
                  pagination={false}
                  rowKey="key"
                  columns={columns}
                  dataSource={rows}
                  scroll={{ y: 'calc(100vh - 410px)' }}
                />
                <div style={{ padding: '16px', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end' }}>
                  <Button 
                    type="primary" 
                    onClick={handleApplyVrioSelection}
                    disabled={Object.values(selectedSevenS).filter(val => val?.chon).length === 0}
                  >
                    {t('bsc.btn.chon', 'Chọn')}
                  </Button>
                </div>
              </div>
            );
          })()}
        </div>
      );
    }

    // Default HTML content
    return (
      <Card className={styles['guide-content-card']}>
        <div className={styles['operation-content']} dangerouslySetInnerHTML={toSafeHtml(modalGoiY ?? '')} />
      </Card>
    );
  };

  return (
    <Modal
      title={t('bsc.label.phongcach', 'Phong Cách & Văn Hóa')}
      open={visible}
      onCancel={onClose}
      width="95vw"
      style={{ maxWidth: 1600, minWidth: 1000 }}
      footer={null}
      destroyOnHidden
      centered
    >
      {loading ? (
        <div className={styles['help-loading']}>
          <Spin size="large" />
        </div>
      ) : (
        <Layout className={styles['help-modal-page']}>
          <Sider width={350} className={styles['help-sidebar']}>
            <div className={styles['help-sidebar-content']}>
              <div className={styles['help-sidebar-header']}>
                <Input 
                  placeholder={t('bsc.placeholder.timkiemhuongdan', 'Tìm kiếm hướng dẫn...')} 
                  value={searchValue} 
                  onChange={(e) => handleSearch(e.target.value)} 
                  prefix={<SearchOutlined />} 
                  allowClear 
                  size="middle" 
                />
              </div>
              <div className={styles['help-sidebar-tree']}>
                <Tree
                  treeData={toAntTreeNodes(filteredTreeData)}
                  selectedKeys={selectedKeys}
                  expandedKeys={expandedKeys}
                  onSelect={handleTreeSelect}
                  onExpand={handleTreeExpand}
                  showLine
                  showIcon
                  blockNode
                  className='help-sidebar-tree-p-2'
                />
              </div>
            </div>
          </Sider>
          <Content className={styles['help-content']}>
            {selectedGuide ? (
              <Tabs 
                key={selectedGuide.id}
                defaultActiveKey="video" 
                size="large" 
                className={styles['help-tabs']} 
                items={[
                  // Video chuyên gia - chỉ hiển thị khi có hasVideo
                  ...(selectedGuide?.hasVideo ? [{
                    key: 'video', 
                    label: t('bsc.label.videochuyengia', 'Video chuyên gia'), 
                    children: (
                      <div key={`video-${selectedGuide.id}`} style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                        {selectedGuide?.linkVideo ? renderYouTubePlayer(selectedGuide?.linkVideo) : 
                          <div className={styles['help-empty-state']}>
                            <Text type="secondary">{t('bsc.msg.khongcovideohuongdan', 'Không có video hướng dẫn')}</Text>
                          </div>
                        }
                      </div>
                    )
                  }] : []),
                  // Video hướng dẫn - chỉ hiển thị khi có linkVideoHuongDan
                  ...(selectedGuide?.hasVideoHuongDan ? [{
                    key: 'instruction', 
                    label: t('bsc.label.videohuongdan', 'Video hướng dẫn'), 
                    children: (
                      <div key={`instruction-${selectedGuide.id}`} style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                        {renderInstructionVideo(selectedGuide?.linkVideoHuongDan)}
                      </div>
                    )
                  }] : []),
                  // Hướng dẫn thao tác - chỉ hiển thị khi có noiDungMoi
                  ...(selectedGuide?.hasHuongDan ? [{
                    key: 'operation', 
                    label: t('bsc.label.huongdanthaotac', 'Hướng dẫn thao tác'), 
                    children: (
                      <div key={`operation-${selectedGuide.id}`} style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                        {renderOperationGuide(selectedGuide.noiDungMoi)}
                      </div>
                    )
                  }] : []),
                  // Gợi ý - chỉ hiển thị khi hasGoiY = true và không phải ModalChienLuoc
                  ...(selectedGuide?.hasGoiY && hasMeaningfulGuideField(selectedGuide?.modalGoiY) && selectedGuide?.modalGoiY !== 'ModalChienLuoc' ? [{
                    key: 'suggestion', 
                    label: t('bsc.label.goiy', 'Gợi ý'), 
                    children: (
                      <div key={`suggestion-${selectedGuide.id}`} style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                        {renderSuggestion(selectedGuide?.modalGoiY)}
                      </div>
                    )
                  }] : []),
                  // Tài liệu - chỉ hiển thị khi có soTrangTaiLieu
                  ...(selectedGuide?.hasTaiLieu ? [{
                    key: 'document', 
                    label: t('bsc.label.tailieu', 'Tài liệu'), 
                    children: (
                      <div key={`document-${selectedGuide.id}`} style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                        {renderDocument(selectedGuide?.soTrangTaiLieu)}
                      </div>
                    )
                  }] : []),
                ].filter(Boolean)} 
              />
            ) : (
              <div className={styles['help-empty-content']}>
                <div style={{ textAlign: 'center' }}>
                  <Text type="secondary" style={{ fontSize: '16px' }}>Vui lòng chọn một mục hướng dẫn từ danh sách bên trái</Text>
                </div>
              </div>
            )}
          </Content>
        </Layout>
      )}
    </Modal>
  );
};

export default HelpModal;
